<template>
  <div class="flex h-screen bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg border-r border-gray-700">
      <div class="flex items-center justify-center">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20" />
      </div>
      <Navbar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md border-b border-gray-700">
        <h2 class="text-xl font-semibold">Admin - Roles</h2>
        <button
          @click="openCreateModal"
          class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700"
        >
          Add Role
        </button>
      </header>

      <main class="flex-1 overflow-y-auto p-6 space-y-6">
        <div class="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
          <h3 class="text-lg font-semibold mb-4">Role List</h3>

          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-700">
                <th class="p-3 rounded-tl-lg">ID</th>
                <th class="p-3">Role Name</th>
                <th class="p-3">Description</th>
                <th class="p-3">Permissions</th>
                <th class="p-3">Created At</th>
                <th class="p-3">Updated At</th>
                <th class="p-3 rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="role in roles"
                :key="role.id"
                class="border-t border-gray-700 hover:bg-gray-700 transition"
              >
                <td class="p-3 font-semibold">#{{ role.id }}</td>
                <td class="p-3">{{ role.name }}</td>
                <td class="p-3">{{ role.description }}</td>
                <td class="p-3">
                  <ul class="list-disc list-inside">
                    <li v-for="perm in role.permissions" :key="perm">{{ perm }}</li>
                  </ul>
                </td>
                <td class="p-3">{{ formatTimestamp(role.created_at) }}</td>
                <td class="p-3">{{ formatTimestamp(role.updated_at) }}</td>
                <td class="p-3 flex space-x-2">
                  <button
                    @click="openEditModal(role)"
                    class="px-3 py-1 bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    @click="deleteRole(role.id)"
                    class="px-3 py-1 bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              <tr v-if="roles.length === 0">
                <td colspan="7" class="text-center py-4 text-gray-400">No roles found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>

    <!-- Modal -->
    <div
      v-if="modalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-gray-800 rounded-lg p-6 w-96 max-h-[90vh] overflow-y-auto">
        <h3 class="text-lg font-semibold mb-4">{{ editingRole ? 'Edit Role' : 'Create Role' }}</h3>

        <div class="mb-4">
          <label class="block mb-1">Role Name</label>
          <input
            v-model="roleForm.name"
            class="w-full p-2 rounded-lg bg-gray-900 border border-gray-700 focus:outline-none"
            type="text"
          />
        </div>

        <div class="mb-4">
          <label class="block mb-1">Description</label>
          <textarea
            v-model="roleForm.description"
            class="w-full p-2 rounded-lg bg-gray-900 border border-gray-700 focus:outline-none"
          ></textarea>
        </div>

        <div class="mb-4">
          <label class="block mb-1">Permissions</label>
          <div class="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-700 rounded-lg p-2 bg-gray-900">
            <div v-for="perm in allPermissions" :key="perm">
              <label class="flex items-center space-x-2">
                <input
                  type="checkbox"
                  :value="perm"
                  v-model="roleForm.permissions"
                  class="accent-blue-600"
                />
                <span>{{ perm }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-2">
          <button @click="closeModal" class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700">Cancel</button>
          <button
            @click="saveRole"
            class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {{ editingRole ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/adminnav.vue';
import axios from 'axios';

export default {
  name: "AdminRolesPage",
  components: { Navbar },
  data() {
    return {
      roles: [],
      modalVisible: false,
      editingRole: null,
      roleForm: {
        name: '',
        description: '',
        permissions: []
      },
      allPermissions: [
        "view_servers",
        "view_orders",
        "view_nodes",
        "view_tickets",
        "view_income_reports",
        "view_affiliates",
        "view_users",
        "view_networks",
        "view_roles",
        "view_dashboard"
      ]
    };
  },
  async mounted() {
    await this.fetchRoles();
  },
  methods: {
    async fetchRoles() {
      try {
        const response = await axios.get(
          'https://api.syniac.com:3000/api/admin/role/get',
          { withCredentials: true }
        );
        this.roles = response.data.roles;
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
    openCreateModal() {
      this.editingRole = null;
      this.roleForm = { name: '', description: '', permissions: [] };
      this.modalVisible = true;
    },
    openEditModal(role) {
      this.editingRole = role;
      this.roleForm = {
        name: role.name,
        description: role.description,
        permissions: [...role.permissions]
      };
      this.modalVisible = true;
    },
    closeModal() {
      this.modalVisible = false;
    },
    async saveRole() {
      try {
        const payload = {
          name: this.roleForm.name,
          description: this.roleForm.description,
          permissions: this.roleForm.permissions
        };

        if (this.editingRole) {
          // Update existing role
          await axios.put(
            `https://api.syniac.com:3000/api/admin/role/update/${this.editingRole.id}`,
            payload,
            { withCredentials: true }
          );
        } else {
          // Create new role
          await axios.post(
            'https://api.syniac.com:3000/api/admin/role/add',
            payload,
            { withCredentials: true }
          );
        }

        await this.fetchRoles();
        this.closeModal();
      } catch (error) {
        console.error("Error saving role:", error);
      }
    },
    async deleteRole(roleID) {
      if (!confirm("Are you sure you want to delete this role?")) return;
      try {
        await axios.delete(
          `https://api.syniac.com:3000/api/admin/role/delete/${roleID}`,
          { withCredentials: true }
        );
        this.roles = this.roles.filter(r => r.id !== roleID);
      } catch (error) {
        console.error("Error deleting role:", error);
      }
    }
  }
};
</script>
